import { useQuery } from "react-query";
import { checkIfUserEligible } from "../services/api";
import { isUserLoggedIn } from "../utils/helpers";
import { waitForFeedGate } from "../utils/feedFirstPaintGate";
// 782: "inf" -> Infinity. See remainingLimit.js for why this exists.
import { normalizeRemainingLimit } from "./remainingLimit";
export { UNLIMITED, normalizeRemainingLimit } from "./remainingLimit";

// T22.120 (Gordon D20): team wheels give every member the team leader's plan
// features. The check therefore names the wheel open in Edit Wheel
// (/builder/:id) or View Wheel (/wheel/:id, /wheelview/:id); the BE decides
// whether it is a team wheel the user belongs to.
export const wheelIdFromPath = (pathname) => {
  const match = /^\/(?:builder|wheel|wheelview)\/(\d+)(?:\/|$)/.exec(pathname || "");
  return match ? Number(match[1]) : null;
};

export const useIfUserEligible = (feature, options = {}) => {
  // Optional flags you can pass from the component
  const { explorePage = false } = options;
  const wheelId = wheelIdFromPath(window.location.pathname);

  const shouldEnableQuery =
    !!feature && (!explorePage || (explorePage && isUserLoggedIn()));

  const {
    data,
    isError,
    isLoading: isCheckingEligibility,
    refetch: checkFeatureEligibility,
  } = useQuery(
    ["userEligibility", feature, wheelId],
    // T22.201: on a cold Discover load the header's eligibility POSTs ran
    // alongside explore page 1 -- wait for page 1 (utils/feedFirstPaintGate.js).
    () =>
      waitForFeedGate().then(() =>
        wheelId ? checkIfUserEligible(feature, wheelId) : checkIfUserEligible(feature)
      ),
    {
      enabled: shouldEnableQuery, // <---- this prevents API call
      refetchOnWindowFocus: true,
      select: (response) => ({
        isEligible: response.data.data.allowed,
        requiredSubscription: response.data.data.required_subscription_tier,
        subscription:
          response.data.data.required_subscription_tier_name || "Premium",
        remainingTokens: normalizeRemainingLimit(
          response.data.data?.extra_data?.remaining_limit
        ),
        isAppropriateSubscription:
          response.data.data?.extra_data?.appropriate_sub,
        isInReferralProgram: response.data.data?.extra_data?.in_ref_program,
        // T22.544: the BE's one-time first-free AI generation is still open.
        firstFreeAvailable:
          response.data.data?.extra_data?.first_free_available === true,
      }),
    }
  );

  return {
    // ADDITIVE (ISP-9999, analytics paywall): the two callers below need to
    // distinguish "the backend SAID you are allowed" from "we never got an
    // answer". `isEligible` deliberately keeps its historic fail-OPEN default
    // (`?? true`) because dozens of existing call sites rely on it and
    // flipping it here would change the running app everywhere at once.
    // A gate that must fail CLOSED reads `isEntitlementResolved` instead and
    // treats unresolved as NOT entitled. Nothing existing changes.
    isEntitlementResolved: data !== undefined,
    isEntitlementError: isError,
    isEligible: data?.isEligible ?? true,
    subscription: data?.subscription ?? "Premium",
    requiredSubscription: data?.requiredSubscription,
    isCheckingEligibility,
    checkFeatureEligibility,
    remainingTokens: data?.remainingTokens ?? 0,
    isAppropriateSubscription: data?.isAppropriateSubscription ?? false,
    isInReferralProgram: data?.isInReferralProgram ?? false,
    firstFreeAvailable: data?.firstFreeAvailable ?? false,
  };
};
